<template>
  <article>
    <activity-stage :customRootContainerClass="'starts-with'">
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content">
        <div class="steps-container">
          <figure class="step-image">
            <img
              alt="Main step image"
              :src="currentStep?.activityImage?.image?.src"
              v-if="currentStep?.activityImage?.image?.src"
            />
          </figure>
          <text-display-list :text-size="textSize" class="text-steps">
            <li
              v-for="(step, stepIndex) in currentRound.steps"
              :key="`${progress.roundIndex}-${stepIndex}`"
              :class="`step step-${stepIndex}`"
            >
              <color-block-text :text="step.answer" :text-step="stepIndex"> </color-block-text>
            </li>
          </text-display-list>
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax, TimelineLite } from 'gsap';

import ActivityManager from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ErrorLogger from 'mixins/ErrorLogger';

import { getCharacterAmountInSteps } from 'utils/activity-util';

import ActivityStage from 'components/activity/ActivityStage.vue';
import ColorBlockText from 'components/text-displays/ColorBlockText.vue';
import TextDisplayList from 'components/text-displays/TextDisplayList.vue';

export default {
  components: {
    ActivityStage,
    ColorBlockText,
    TextDisplayList
  },
  mixins: [TypingActivityHelper, ActivityManager, ErrorLogger],
  data() {
    return {
      activityName: 'StartsWith'
    };
  },
  beforeDestroy() {
    TweenMax.killAll();
  },
  computed: {
    textSize() {
      return getCharacterAmountInSteps(this.currentRound.steps || []) > 45
        ? 'medium'
        : 'medium-large';
    }
  },
  methods: {
    async loadActivity() {
      this.createRounds();
      this.sfx = await this.loadSounds();
    },

    async loadImages() {
      this.rounds.forEach(round => {
        round.steps.forEach(step => this.queueImage(step.activityImage?.image?.src));
      });

      return this.downloadImages();
    },

    async loadSounds() {
      this.rounds.forEach(({ steps }) => {
        steps.forEach(({ answerAudio }) => {
          this.queueAudio(answerAudio?.audio?.src);
        });
      });

      const cache = await this.downloadAudio();

      return this.rounds.map(({ steps }) =>
        steps.map(({ answerAudio }) => cache.get(answerAudio?.audio?.src))
      );
    },

    getKeyboardConfig() {
      const { keyboardType, roofs } = this.activityData.keyboard;
      return {
        keyboardType,
        roofs
      };
    },

    startActivity() {
      this.setupNewRound();
      this.enableKeyboard();
      this.errorLoggerStart();

      this.$nextTick(this.setActivityContainerHeight);

      this.$nextTick(() => {
        this.hideAllSteps();
        this.fadeInStep(this.progress.stepIndex);
      });
    },

    finishStep() {
      this.completeStep();
      this.setCurrentStep();
    },

    handleCorrectKey(attempt) {
      this.sndType();
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      if (this.isEndOfCurrentWord) {
        this.handleStepEnd();
      }

      if (this.isEndOfRound) {
        this.handleRoundEnd();
      }
    },

    handleStepEnd() {
      this.sndBell();
      this.sfx[this.progress.roundIndex][this.progress.stepIndex].play();
      this.finishStep();
      this.fadeInStep(this.progress.stepIndex);
    },

    handleRoundEnd() {
      const timeline = new TimelineLite();

      if (this.isEndOfActivity) {
        timeline.delay(1).call(this.completeActivity);
      } else {
        timeline
          .to('.steps-container', 0.5, { opacity: 0 })
          .call(this.setupNewRound)
          .call(this.$nextTick, [
            () =>
              timeline
                .set('.step', { opacity: 0 })
                .set(`.step-${this.progress.stepIndex}`, { opacity: 1 })
                .to('.steps-container', 0.5, { opacity: 1 })
          ]);
      }
    },

    fadeInStep(stepIndex) {
      TweenMax.to(`.step-${stepIndex}`, 0.25, { opacity: 1 });
    },

    hideAllSteps() {
      TweenMax.set('.step', { opacity: 0 });
    }
  },
  watch: {
    currentStep(newVal) {
      if (newVal?.answer) this.setTotalKeyboardingSegment(newVal.answer);
    }
  }
};
</script>

<style lang="scss" scoped>
.starts-with {
  ul {
    align-items: center;
    display: flex;
    flex-flow: row wrap;
    justify-content: space-around;
    list-style-type: none;
    margin: 0;
    padding: 20px;
  }

  p {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    font-family: hwtprint;
  }

  .step-image {
    height: 200px;
    margin: auto;
    text-align: center;

    img {
      height: 100%;

      width: auto;
    }
  }
}

.chrome-os {
  .starts-with {
    .step-image {
      height: 175px !important;
      margin: 0px;
      min-height: 175px !important;

      img {
        height: 100%;
        width: auto;
      }
    }
  }
}

.tablet {
  .starts-with {
    .step-image {
      height: 175px !important;
      min-height: 175px !important;

      img {
        height: 100%;
        width: auto;
      }
    }
  }
}
</style>
